/**
 * DiscountBanner — slim success banner above the rate cards when an unlock
 * returned an AI-granted discount (state.route.discount = DiscountGrant
 * { id, code, percent, expiresAt }). Style-matched to RfqResultsBanner's
 * banner row; copy per the signed-off sheet (notes/public-rate-gate-mockup.html
 * §3/§5). The prices themselves come back already discounted from the search
 * re-run (queryParams.discount → discountCode), so this banner only announces.
 *
 * Renders in BOTH result containers (Home.js AND search_component.js — the
 * two-container gotcha) inside a wrapper div so the production build's
 * constant-element optimizer can't drop it.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { color, radius, transition } from '../../styles/designTokens';

const Banner = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: ${color.okSoft};
  border: 1px solid ${color.okLine};
  border-radius: ${radius.base};
  font-size: 13.5px;
  line-height: 1.55;
  color: ${color.ink};
  transition: ${transition};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Emphasis = styled.b`
  color: ${color.ok};
`;

const Meta = styled.span`
  color: ${color.ink2};
  white-space: nowrap;
`;

const formatValidUntil = (expiresAt) => {
  if (!expiresAt) return null;
  const parsed = moment(expiresAt);
  return parsed.isValid() ? parsed.format('MMM D') : null;
};

export const DiscountBannerView = ({ discount }) => {
  if (!discount || !discount.id || !discount.code) return null;

  const percent = Number(discount.percent);
  if (!percent || percent <= 0) return null;

  const validUntil = formatValidUntil(discount.expiresAt);

  return (
    <Banner role="status">
      <span>
        <span role="img" aria-label="celebration">
          🎉
        </span>{' '}
        <Emphasis>{percent}% off these rates</Emphasis> has been applied for
        you automatically.
      </span>
      <Meta>
        Code {discount.code}
        {validUntil ? ` · valid until ${validUntil}` : ''}
      </Meta>
    </Banner>
  );
};

DiscountBannerView.propTypes = {
  /** DiscountGrant from the unlock response ({ id, code, percent, expiresAt }) */
  discount: PropTypes.object,
};

const mapStateToProps = (state) => ({
  discount: state.route.discount,
});

export default connect(mapStateToProps)(DiscountBannerView);
